import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import PlaceCard from "./PlaceCard";
import AccommodationCard from "./AccommodationCard";
import { accommodationToContentItem } from "./AccommodationCard";
import useToraStore from "@/stores/tripBuilder/useToraStore";
import useTransportStore from "@/stores/tripBuilder/useTransport";
import useCurrencyStore from "@/stores/ui/currencyStore";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { Search, ArrowLeft } from "lucide-react";
import FlightCard from "@tripbuilder/cat-transport/right/FlightCard";
import logger from "@/utils/logger";

// Modals
import FlightModal from "@tripbuilder/cat-transport/right/FlightModal";

// Types
import { ServerFlightInfo } from "@/types/flights";
import { TransportLeg, TripTransport, Trip, TransportData } from "@/types/trip";

// Loading Card
const LoadingCard = () => {
  return (
    <div className="rounded-2xl border-neutral-200 border-1 my-3 transition-all duration-300 ease-in-out animate-pulse">
      <div className="p-3 sm:p-3">
        <div className="flex flex-col sm:flex-row items-start gap-4 h-[9rem]">
          {/* Image Section */}
          <div className="w-full sm:w-[10rem] h-[9rem] bg-neutral-200 rounded-lg"></div>

          {/* Content Section */}
          <div className="flex-1 min-w-0 flex flex-col h-full justify-between w-full">
            <div className="w-full">
              <div className="h-5 bg-neutral-200 rounded-md w-3/4 mb-4"></div>
              <div className="h-3 bg-neutral-200 rounded-md w-full mb-2"></div>
              <div className="h-3 bg-neutral-200 rounded-md w-5/6"></div>
            </div>

            {/* Actions Section */}
            <div className="flex justify-between items-center w-full">
              <div className="flex flex-row items-center space-x-3 mt-1">
                <div className="bg-neutral-200 rounded-full w-16 h-6"></div>
                <div className="bg-neutral-200 rounded-full w-16 h-6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const convertFlightToTripTransport = (
  flight: ServerFlightInfo,
  fromLocationId: string,
  toLocationId: string
): TripTransport => {
  const legs: TransportLeg[] = flight.route.map((route) => ({
    id: `${route.flight_number}-${route.departure_utc}`,
    mode: "flight",
    provider: {
      name: route.airline,
      logo: `https://images.kiwi.com/airlines/128x128/${route.short_airline}.png?default=airline.png`,
      type: "airline",
      code: route.short_airline,
    },
    departure: {
      location: {
        name: route.from.city,
        code: route.from.airport,
        coordinates: {
          latitude: parseFloat(route.from.latitude),
          longitude: parseFloat(route.from.longitude),
        },
        timezone: "UTC",
      },
      time: {
        local: route.departure_local,
        utc: route.departure_utc,
      },
    },
    arrival: {
      location: {
        name: route.to.city,
        code: route.to.airport,
        coordinates: {
          latitude: parseFloat(route.to.latitude),
          longitude: parseFloat(route.to.longitude),
        },
        timezone: "UTC",
      },
      time: {
        local: route.arrival_local,
        utc: route.arrival_utc,
      },
    },
    reference: `${route.short_airline}${route.flight_number}`,
    duration: route.duration,
    booking_info: {
      confirmation: flight.booking_token,
      price: flight.price,
      currency: "USD",
      booking_link: flight.booking_link,
    },
  }));

  return {
    id: flight.id,
    fromLocationId,
    toLocationId,
    type: "api",
    legs,
    total_price: flight.price,
    currency: "USD",
    metadata: {
      api_booking_token: flight.booking_token,
      api_search_id: flight.id,
    },
  };
};

const ToraRecommendations: React.FC = () => {
  const {
    setShowRecommendations,
    setShowAccommodations,
    suggestions,
    experiences,
    flights,
    accommodations,
    isLoading,
  } = useToraStore();

  const { transportData } = useTransportStore();
  const { updateTripData, tripData } = useTripStore();
  const isAdmin = useTripStore((state) => state.checkIsAdmin());
  const selectedCurrency = useCurrencyStore((state) => state.selectedCurrency);
  const selectedLocation = useTripStore((state) => state.selectedLocation);

  // Add state for flight modal
  const [flightModalOpen, setFlightModalOpen] = useState(false);
  const [selectedTripTransport, setSelectedTripTransport] =
    useState<TripTransport | null>(null);

  const handleCloseModal = () => {
    setFlightModalOpen(false);
    setSelectedTripTransport(null);
  };

  const handleSelectFlight = useCallback(
    (flight: ServerFlightInfo) => {
      const tripTransport = convertFlightToTripTransport(
        flight,
        transportData.locationAbove?.id || "",
        transportData.locationBelow?.id || ""
      );
      setSelectedTripTransport(tripTransport);
      setFlightModalOpen(true);
    },
    [setFlightModalOpen, transportData]
  );

  const addOrReplaceFlightInTrip = useCallback(
    (trip: Trip, flight: ServerFlightInfo, transportData: TransportData) => {
      logger.log("Adding flight to trip", flight);
      const isArrival = !selectedLocation?.airports?.includes(
        flight.from.airport
      );
      const fromLocationId = isArrival
        ? transportData.locationAbove?.id || ""
        : selectedLocation?.id || "";
      const toLocationId = isArrival
        ? selectedLocation?.id || ""
        : transportData.locationBelow?.id || "";

      const updatedTrip = { ...trip };

      const existingTransportIndex = updatedTrip.transport.findIndex(
        (t) =>
          t.fromLocationId === fromLocationId && t.toLocationId === toLocationId
      );

      const newTransport: TripTransport = {
        id: flight.id,
        fromLocationId,
        toLocationId,
        type: "api",
        legs:
          flight.route?.map((routeLeg) => ({
            id: `${flight.id}-${routeLeg.flight_number}`,
            mode: "flight",
            provider: {
              name: routeLeg.airline || "Unknown Airline",
              code: routeLeg.short_airline || "UNK",
              type: "airline",
              logo: `https://images.kiwi.com/airlines/64x64/${routeLeg.short_airline}.png`,
            },
            departure: {
              location: {
                name: routeLeg.from.city,
                code: routeLeg.from.airport,
                coordinates: {
                  latitude: parseFloat(routeLeg.from.latitude),
                  longitude: parseFloat(routeLeg.from.longitude),
                },
                timezone: "UTC",
              },
              time: {
                utc: routeLeg.departure_utc,
                local: routeLeg.departure_local,
              },
            },
            arrival: {
              location: {
                name: routeLeg.to.city,
                code: routeLeg.to.airport,
                coordinates: {
                  latitude: parseFloat(routeLeg.to.latitude),
                  longitude: parseFloat(routeLeg.to.longitude),
                },
                timezone: "UTC",
              },
              time: {
                utc: routeLeg.arrival_utc,
                local: routeLeg.arrival_local,
              },
            },
            reference: routeLeg.flight_number || "",
            duration: routeLeg.duration,
          })) || [],
        total_price: flight.price,
        currency: selectedCurrency.code,
        metadata: {
          api_booking_token: flight.booking_token,
          api_search_id: flight.id,
          api_booking_link: flight.booking_link,
        },
      };

      if (existingTransportIndex !== -1) {
        // Replace existing transport
        updatedTrip.transport[existingTransportIndex] = newTransport;
      } else {
        // Add new transport
        updatedTrip.transport.push(newTransport);
      }

      updateTripData(updatedTrip);
      // close recommendations
      setShowRecommendations(false);
    },
    [updateTripData, selectedCurrency, selectedLocation, setShowRecommendations]
  );

  const handleAddFlight = useCallback(
    (flight: ServerFlightInfo) => {
      if (!tripData) return;
      addOrReplaceFlightInTrip(tripData, flight, transportData);
    },
    [addOrReplaceFlightInTrip, tripData, transportData]
  );

  const hasExperiences = experiences.length > 0;
  const hasFlights = flights.length > 0;
  const hasSuggestions = suggestions.length > 0;
  const hasAccommodations = accommodations.length > 0;
  const hasContent =
    hasExperiences || hasFlights || hasSuggestions || hasAccommodations;

  console.log("accommodations from tora recommendations", accommodations);

  // Default to UTC if locations are not available
  const selectedLocationTimezone = selectedLocation?.date?.time_zone || "UTC";
  const aboveLocationTimezone =
    transportData.locationAbove?.date?.time_zone || "UTC";
  const belowLocationTimezone =
    transportData.locationBelow?.date?.time_zone || "UTC";

  return (
    <div className="w-full h-[calc(100vh-1.8rem)] flex flex-col items-center justify-start mt-2">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-neutral-100/70 rounded-lg py-3 px-4 w-full"
      >
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center space-x-4">
            <Search className="w-5 h-5 text-neutral-600" />
            <div className="flex flex-col items-start">
              <p className="text-sm text-neutral-700 font-medium mb-0.5">
                Tora Recommendations
              </p>
              <p className="text-xs text-neutral-500 font-light">
                Results based on chat history
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setShowRecommendations(false);
              setShowAccommodations(false);
            }}
            className="space-x-2 flex items-center bg-indigo-800 rounded-md py-2 px-3"
          >
            <ArrowLeft className="w-3 h-3 text-white" />
            <h2 className="text-xs font-medium text-white">Back</h2>
          </button>
        </div>
      </motion.div>

      <motion.div
        className="my-2 w-full flex-grow rounded-xl overflow-y-auto no-scrollbar"
        style={{
          maxHeight: "calc(100vh - 7rem)",
        }}
      >
        {isLoading ? (
          // Display loading cards when loading
          Array.from({ length: 5 }, (_, index) => (
            <motion.div
              key={`loading-${index}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="w-full"
            >
              <LoadingCard />
            </motion.div>
          ))
        ) : hasContent ? (
          <>
            {/* Flight Recommendations */}
            {hasFlights && (
              <div className="p-1 space-y-4">
                {flights.map((flight, index) => {
                  // For each flight, determine if it's arriving or departing
                  // and set the appropriate timezones
                  const isArriving = !selectedLocation?.airports?.includes(
                    flight.from.airport
                  );

                  // Set departure and arrival timezones based on direction
                  const departureTimeZone = isArriving
                    ? aboveLocationTimezone
                    : selectedLocationTimezone;

                  const arrivalTimeZone = isArriving
                    ? selectedLocationTimezone
                    : belowLocationTimezone;

                  return (
                    <motion.div
                      key={`flight-${flight.id}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="w-full"
                    >
                      <FlightCard
                        flight={flight}
                        onSelect={() => handleSelectFlight(flight)}
                        onAddFlight={() => handleAddFlight(flight)}
                        departureTimeZone={departureTimeZone}
                        arrivalTimeZone={arrivalTimeZone}
                        selectedCurrencyCode={selectedCurrency.code}
                        isAdmin={isAdmin}
                      />
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Experience Recommendations */}
            {hasExperiences && (
              <div>
                {experiences.map((experience, index) => (
                  <motion.div
                    key={`experience-${experience.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="w-full"
                  >
                    <PlaceCard
                      type="place_details"
                      place={experience}
                      isSelected={false}
                      onSelect={() => {}}
                    />
                  </motion.div>
                ))}
              </div>
            )}

            {/* Legacy Suggestions */}
            {hasSuggestions && (
              <div>
                {suggestions.map((item, index) => (
                  <motion.div
                    key={item.id || `suggestion-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="w-full"
                  >
                    <PlaceCard
                      type="place_details"
                      place={item}
                      isSelected={false}
                      onSelect={() => {}}
                    />
                  </motion.div>
                ))}
              </div>
            )}

            {/* Accommodation Recommendations */}
            {hasAccommodations && (
              <div className="w-full grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3">
                {accommodations.map((accommodation, index) => (
                  <motion.div
                    key={`accommodation-${accommodation.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="w-full"
                  >
                    <AccommodationCard
                      accommodation={accommodation}
                      onSelect={() => {}}
                      isAdmin={isAdmin}
                    />
                  </motion.div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-full w-full">
            <p className="text-neutral-500 text-sm">No recommendations yet</p>
          </div>
        )}
        <AnimatePresence mode="wait">
          {flightModalOpen && selectedTripTransport && (
            <FlightModal
              flight={selectedTripTransport}
              isOpen={flightModalOpen}
              handleClose={handleCloseModal}
              transportData={transportData}
              toraRecommendation={true}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default ToraRecommendations;
